import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Handle, Position } from "@xyflow/react";
import { HelpCircle, Users } from "lucide-react";
import { SYSTEM_ICON } from "../../../../../constants/entity-icons";
import { useNodeVisualState } from "../hooks/useNodeVisualState";
import { START_END_CIRCLE_INSET, START_END_CIRCLE_SIZE, START_END_HANDLE_Y, START_END_HEIGHT, START_END_LABEL_Y, START_END_WIDTH } from "../constants";
// Amber palette for the UNCLEAR variant. Mirrors the dashed "needs attention"
// styling used on system/external swimlanes so reviewers read the signal
// consistently.
const UNCLEAR_STROKE = "#F59E0B";
const UNCLEAR_FILL = "#FFFBEB";
function ProcessFlowEndNode({ data }) {
    const { t } = useTranslation();
    const { tokens, dimClass, handleMouseEnter, handleMouseLeave } = useNodeVisualState({
        isSelected: data?.isSelected,
        isOnPath: data?.isOnPath,
        isDimmed: data?.isDimmed
    });
    if (!data) return null;
    const endLabel = data.reasonForEnd ? `${t("processFlow.endLabel")}: ${data.reasonForEnd}` : t("processFlow.endLabel");
    const isUnclear = data.variant === "unclear";
    // Domain icons (system / external contact) keep their own coloring; the
    // plain end state renders as a bordered circle sharing the token colors.
    // UNCLEAR takes precedence: a help-circle supersedes domain icons since
    // the reviewer needs the owner-action cue first.
    const isSystemEnd = data.reasonForEnd?.startsWith("System(s) <");
    const isExternalEnd = data.reasonForEnd?.startsWith("External Contact(s) <");
    const icon = isUnclear ? /*#__PURE__*/ _jsx(HelpCircle, {
        size: 28,
        className: "text-warning pointer-events-none"
    }) : isSystemEnd ? /*#__PURE__*/ _jsx(SYSTEM_ICON, {
        size: 28,
        className: "text-muted-foreground pointer-events-none"
    }) : isExternalEnd ? /*#__PURE__*/ _jsx(Users, {
        size: 28,
        className: "text-muted-foreground pointer-events-none"
    }) : null;
    const borderStyle = isUnclear ? "dashed" : "solid";
    const borderColor = isUnclear ? UNCLEAR_STROKE : tokens.stroke;
    const backgroundColor = isUnclear ? UNCLEAR_FILL : tokens.fill;
    return /*#__PURE__*/ _jsxs("div", {
        className: `relative text-[12px] cursor-pointer transition-opacity duration-200 ${dimClass} ${tokens.textClass}`,
        style: {
            width: START_END_WIDTH,
            height: START_END_HEIGHT
        },
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        children: [
            /*#__PURE__*/ _jsx(Handle, {
                type: "target",
                position: Position.Left,
                style: {
                    top: START_END_HANDLE_Y
                },
                isConnectable: false
            }),
            /*#__PURE__*/ _jsx("div", {
                className: `absolute rounded-full border-2 flex items-center justify-center ${tokens.ringClass}`,
                style: {
                    left: START_END_CIRCLE_INSET,
                    top: START_END_CIRCLE_INSET,
                    width: START_END_CIRCLE_SIZE,
                    height: START_END_CIRCLE_SIZE,
                    borderColor,
                    borderStyle,
                    backgroundColor
                },
                children: icon
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "absolute left-1/2 transform -translate-x-1/2 text-center whitespace-nowrap",
                style: {
                    top: START_END_LABEL_Y
                },
                children: endLabel
            })
        ]
    });
}
export default /*#__PURE__*/ memo(ProcessFlowEndNode);

//# sourceMappingURL=EndNode.js.map